import React, { useState, useEffect, useCallback, useRef } from "react";
import { getCookie, setCookie } from "../../utils/cookies";
import Button from "@mui/material/Button";
import CurtainIcon from "../../assets/images/curtain.svg";
import ApplauseSound from "../../assets/audio/applause.mp3";
import "../../assets/styles/launching.css";

interface LaunchingProps {
  onComplete: () => void;
}

const Launching = ({ onComplete }: LaunchingProps) => {
  const [isLaunched, setIsLaunched] = useState(false);
  const [animations, setAnimations] = useState<any[]>([]);
  const [isExiting, setIsExiting] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    if (getCookie("launchingScreen")) {
      onComplete();
    }
  }, [onComplete]);

  const cutRibbon = useCallback(() => {
    setIsLaunched(true);
    launchAnimations();

    // Play applause sound
    if (audioRef.current) {
      audioRef.current.play();
    }

    setTimeout(() => {
      const fadeDuration = 2000;
      const fadeStep = 0.05;
      const intervalTime = fadeDuration / (1 / fadeStep);

      const fadeAudio = setInterval(() => {
        if (audioRef.current && audioRef.current.volume > 0) {
          audioRef.current.volume = Math.max(
            0,
            audioRef.current.volume - fadeStep,
          );
        } else {
          clearInterval(fadeAudio);
        }
      }, intervalTime);

      // setIsExiting(true);
    }, 8000); // start fade at 8s, end at 10s
  }, [onComplete]);

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && !isLaunched) {
        cutRibbon();
      }
    },
    [isLaunched, cutRibbon],
  );

  const handleComplete = useCallback(() => {
    if (!isLaunched) return;

    // Start fade animation
    setIsExiting(true);

    // After fade completes, navigate
    setTimeout(() => {
      setCookie("launchingScreen", false, 30);
      onComplete();
    }, 1000); // match CSS fade duration
  }, [isLaunched, onComplete]);

  const launchAnimations = useCallback(() => {
    const newAnimations: any[] = [];

    const timestamp = Date.now();

    // 🎉 Party Poppers
    for (let i = 0; i < 6; i++) {
      newAnimations.push({
        id: `popper-${timestamp}-${i}`,
        type: "partyPopper",
        style: {
          left: `${20 + i * 12}%`,
          animationDelay: `${i * 0.3}s`,
        },
      });
    }

    // 🎊 Confetti
    for (let i = 0; i < 100; i++) {
      newAnimations.push({
        id: `confetti-${timestamp}-${i}`,
        type: "confetti",
        style: {
          left: `${Math.random() * 100}%`,
          backgroundColor: `hsl(${Math.random() * 360}, 100%, 50%)`,
          animationDelay: `${Math.random() * 1}s`,
          animationDuration: `${1 + Math.random() * 2}s`,
          transform: `rotate(${Math.random() * 360}deg)`,
          "--tx": `${Math.random() * 100 - 50}px`,
        },
      });
    }

    // ✨ Shimmer overlay
    newAnimations.push({
      id: `shimmer-${timestamp}`,
      type: "shimmer",
      style: {
        animationDuration: "2s",
        opacity: 0.7,
      },
    });

    // 🌟 Stars
    for (let i = 0; i < 20; i++) {
      newAnimations.push({
        id: `star-${timestamp}-${i}`,
        type: "scaleRotate",
        style: {
          left: `${Math.random() * 100}%`,
          top: `${Math.random() * 100}%`,
          animationDelay: `${Math.random() * 0.5}s`,
          animationDuration: `${0.5 + Math.random() * 1}s`,
          backgroundColor: `hsl(${Math.random() * 360}, 100%, 50%)`,
        },
      });
    }

    // 🚀 Bottom Confetti Rockets (Left & Right)
    for (let i = 0; i < 40; i++) {
      newAnimations.push({
        id: `rocket-${timestamp}-${i}`,
        type: "rocket",
        style: {
          left: i % 2 === 0 ? "5%" : "95%",
          bottom: "0%",
          backgroundColor: `hsl(${Math.random() * 360}, 100%, 50%)`,
          animationDelay: `${Math.random() * 0.3}s`,
          animationDuration: `${1 + Math.random()}s`,
          "--tx": `${Math.random() * 200 - 100}px`,
        },
      });
    }

    // 🎇 Crackers Burst From Bottom Center
    for (let i = 0; i < 30; i++) {
      newAnimations.push({
        id: `cracker-${timestamp}-${i}`,
        type: "cracker",
        style: {
          left: "50%",
          bottom: "0%",
          backgroundColor: `hsl(${Math.random() * 360}, 100%, 50%)`,
          animationDelay: `${Math.random() * 0.5}s`,
          "--angle": `${Math.random() * 360}deg`,
        },
      });
    }

    setAnimations(newAnimations);
  }, []);

  if (getCookie("launchingScreen")) return null;

  return (
    <div
      className={`launch-container 
    ${isLaunched ? "digital-inaugural-bg" : ""} 
    ${isExiting ? "launch-exit" : ""}`}
      onClick={handleComplete}
    >
      {/* Applause Audio */}
      <audio ref={audioRef} src={ApplauseSound} preload="auto" />

      {/* Curtains */}
      <div
        className={`launch-curtain launch-curtain-left ${isLaunched ? "launch-curtain-open" : ""}`}
      >
        <img src={CurtainIcon} className="curtain-left" alt="" />
      </div>
      <div
        className={`launch-curtain launch-curtain-right ${isLaunched ? "launch-curtain-open" : ""}`}
      >
        <img src={CurtainIcon} className="curtain-right scale-x-[-1]" alt="" />
      </div>

      {/* Ribbon */}
      <div className={`launch-ribbon ${isLaunched ? "launch-hidden" : ""}`} />

      {/* Scissors */}
      <Button
        variant="text"
        className={`launch-scissors ${isLaunched ? "launch-hidden" : ""}`}
        onClick={cutRibbon}
        onKeyDown={handleKeyPress}
        tabIndex={0}
        aria-label="Cut ribbon to start"
      >
        ✂️
      </Button>

      {/* Animations */}
      <div className="launch-animations">
        {animations.map((animation) => (
          <div
            key={animation.id}
            className={`launch-${animation.type}`}
            style={animation.style}
          />
        ))}
      </div>
    </div>
  );
};

export default Launching;
